import { process, State } from '@progress/kendo-data-query';
import {
  Grid,
  GridColumn,
  GridCustomCellProps,
  GridDataStateChangeEvent,
} from '@progress/kendo-react-grid';
import React, { ReactElement, useMemo } from 'react';

import { EditDescriptor } from '@progress/kendo-react-data-tools';
import { loadMessages, LocalizationProvider } from '@progress/kendo-react-intl';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import {
  BBoxLayer,
  BBoxRect,
} from '../../../Interfaces/Config/BboxConfigSetting';
import {
  ConfigSetting,
  EditConfigSetting,
} from '../../../Interfaces/Config/ConfigSetting';
import { ApplicationState } from '../../../store';
import { fetchConfigNodeImage } from '../../../store/Config/Settings/ConfigSettingsActions';
import esMessages from '../../../utils/gridMessageKey.json';
import { isBBoxRectArray, isBBoxRectObject } from '../../../utils/utils';
import { LocaleInterface, locales } from '../../AlertsGrid/GridLocale';
import { GetString } from '../../StringResourceManager';
import BboxAnnotatorDialog from './BboxAnnotatorDialog';
loadMessages(esMessages, 'fr-CA');

interface Props {
  configSettings: ConfigSetting[];
  uuid: string;
  onSettingsChange: (rows: EditConfigSetting[]) => void;
  onRegisterOpenBBox?: (fn: () => void) => void;
  resetTrigger: number;
}

const DATA_ITEM_KEY = 'ID';

type GridEditContextType = {
  updateNewValue: (rowId: number, value: string) => void;
  isNewValueEditable: (row: EditConfigSetting) => boolean;
};

const GridEditContext = React.createContext<GridEditContextType | null>(null);

const NewValueCell = (props: GridCustomCellProps) => {
  const ctx = React.useContext(GridEditContext);
  if (!ctx) return <td {...props.tdProps} />;

  const row = props.dataItem as EditConfigSetting;
  const value = row.NewValue ?? '';
  const editable = ctx.isNewValueEditable(row);
  return (
    <td {...props.tdProps}>
      <input
        className="k-input k-textbox"
        style={{ width: '100%' }}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          ctx.updateNewValue(row.ID, e.target.value);
        }}
        onClick={e => e.stopPropagation()}
      />
    </td>
  );
};

const JsonNodeConfigDetailComponent: React.FC<Props> = ({
  configSettings,
  uuid,
  onSettingsChange,
  onRegisterOpenBBox,
  resetTrigger,
}): ReactElement => {
  const initialDataState: State = {
    group: [{ field: 'Setting.Category' }, { field: 'Setting.Path' }],
  };

  const [rows, setRows] = React.useState<EditConfigSetting[]>([]);

  const [dataState, setDataState] = React.useState<State>(initialDataState);

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );
  const [bboxDialogOpen, setBboxDialogOpen] = React.useState(false);

  const dispatch: Dispatch<any> = useDispatch();

  const bboxImageUrl = useSelector(
    (state: ApplicationState) => state.configSettingsReducer.nodeImageUrl,
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage,
  );

  const stripCategoryFromPath = (path?: string, category?: string): string => {
    if (!path || !category) return path ?? '';

    const normalizedPath = path.trim().replace(/^\/+|\/+$/g, '');
    const normalizedCategory = category.trim();

    if (normalizedPath === normalizedCategory) {
      return '';
    }

    const prefix = `${normalizedCategory}/`;

    if (normalizedPath.startsWith(prefix)) {
      return normalizedPath.substring(prefix.length);
    }

    return normalizedPath;
  };

  useEffect(() => {
    fetchConfigNodeImage(dispatch, uuid);
  }, []);

  useEffect(() => {
    if (onRegisterOpenBBox) {
      onRegisterOpenBBox(() => {
        setBboxDialogOpen(true);
      });
    }
  }, [onRegisterOpenBBox]);

  useEffect(() => {
    onSettingsChange(rows);
  }, [rows]);

  useEffect(() => {
    if (!configSettings) return;

    const transformedData = configSettings.map(item => ({
      ...item,
      Path: stripCategoryFromPath(item.Path, item.Category),
    }));

    const allEdit: EditDescriptor = {};
    const allData = transformedData.map((item, index) => {
      allEdit[index] = true;
      return {
        ID: index,
        Setting: item,
        OriginalValue: item.Value,
        NewValue: '',
        IsDirty: false,
      };
    });

    setRows(allData);
    return () => {};
  }, [configSettings, resetTrigger]);

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const isBBoxLikeValue = (raw: string): boolean => {
    if (!raw || typeof raw !== 'string') return false;

    try {
      const parsed = JSON.parse(raw);

      if (isBBoxRectObject(parsed)) return true;
      if (isBBoxRectArray(parsed)) return true;

      if (parsed && typeof parsed === 'object') {
        return Object.values(parsed).some(v => {
          return isBBoxRectObject(v) || isBBoxRectArray(v);
        });
      }

      return false;
    } catch {
      return false;
    }
  };

  const bboxRows = useMemo(() => {
    return rows.filter(row => {
      const effectiveValue =
        row.NewValue && row.NewValue.trim() !== ''
          ? row.NewValue
          : row.OriginalValue;

      return isBBoxLikeValue(effectiveValue);
    });
  }, [rows]);

  const serializeRectsForRow = (
    rects: BBoxRect[],
    originalRaw: string,
  ): string => {
    try {
      const parsed: unknown = JSON.parse(originalRaw);

      if (isBBoxRectObject(parsed)) {
        const first = rects[0] ?? { x: 0, y: 0, w: 0, h: 0 };
        return JSON.stringify({
          h: first.h,
          w: first.w,
          x: first.x,
          y: first.y,
        });
      }

      if (Array.isArray(parsed)) {
        return JSON.stringify(
          rects.map(r => ({
            h: r.h,
            w: r.w,
            x: r.x,
            y: r.y,
          })),
        );
      }

      return JSON.stringify(
        rects.map(r => ({
          h: r.h,
          w: r.w,
          x: r.x,
          y: r.y,
        })),
      );
    } catch {
      return JSON.stringify(
        rects.map(r => ({
          h: r.h,
          w: r.w,
          x: r.x,
          y: r.y,
        })),
      );
    }
  };

  const applyBBoxLayerChanges = (updatedLayers: BBoxLayer[]) => {
    setRows(prev =>
      prev.map(row => {
        const matchingLayer = updatedLayers.find(
          layer => layer.rowId === row.ID,
        );
        if (!matchingLayer) return row;

        if (!matchingLayer.rects || matchingLayer.rects.length === 0) {
          return row;
        }

        const originalForShape =
          row.NewValue && row.NewValue.trim() !== ''
            ? row.NewValue
            : row.OriginalValue;

        const serialized = serializeRectsForRow(
          matchingLayer.rects,
          originalForShape,
        );
        return {
          ...row,
          NewValue: serialized,
          IsDirty: serialized.trim() !== '' && serialized !== row.OriginalValue,
        };
      }),
    );
    setBboxDialogOpen(false);
  };

  const updateNewValue = React.useCallback((rowId: number, value: string) => {
    setRows(prev =>
      prev.map(row => {
        if (row.ID !== rowId) return row;

        return {
          ...row,
          NewValue: value,
          IsDirty: value.trim() !== '' && value !== row.OriginalValue,
        };
      }),
    );
  }, []);

  const isNewValueEditable = React.useCallback(
    (row: EditConfigSetting): boolean => {
      return !isBBoxLikeValue(row.Setting.Value);
    },
    [],
  );

  const gridEditContextValue = React.useMemo(
    () => ({
      updateNewValue,
      isNewValueEditable,
    }),
    [updateNewValue, isNewValueEditable],
  );

  return (
    <LocalizationProvider language={currentLocale.language}>
      <GridEditContext.Provider value={gridEditContextValue}>
        <Grid
          style={{ height: '100%', width: '100%', maxWidth: '1250px' }}
          sortable={true}
          filterable={true}
          resizable={true}
          groupable={true}
          pageable={{
            buttonCount: 4,
            pageSizes: [10, 20, 50, 100, 500],
          }}
          take={100}
          data={process(rows, dataState)}
          {...dataState}
          onDataStateChange={(e: GridDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}
          dataItemKey={DATA_ITEM_KEY}
        >
          <GridColumn
            field="Setting.Category"
            title={GetString('COLUMN_TITLE_LANE_CATEGORY')}
            width="200px"
            editable={false}
          />
          <GridColumn
            field="Setting.Path"
            title={GetString('COLUMN_TITLE_LANE_SUB_CATEGORY')}
            width="200px"
            editable={false}
          />

          <GridColumn
            field="Setting.Name"
            title={GetString('COLUMN_TITLE_LANE_NAME')}
            width="210px"
            editable={false}
          />
          <GridColumn
            field="Setting.Value"
            title={GetString('COLUMN_TITLE_LANE_VALUE')}
            width="240px"
            editable={false}
          />

          <GridColumn
            field="NewValue"
            title={GetString('NEW_VALUE')}
            width="280px"
            editor="text"
            editable={true}
            cells={{ data: NewValueCell }}
          />
        </Grid>
        {bboxDialogOpen && (
          <BboxAnnotatorDialog
            open={bboxDialogOpen}
            imageUrl={bboxImageUrl}
            bboxRows={bboxRows}
            onClose={() => setBboxDialogOpen(false)}
            onSave={applyBBoxLayerChanges}
          />
        )}
      </GridEditContext.Provider>
    </LocalizationProvider>
  );
};

export default JsonNodeConfigDetailComponent;
